import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { getAccount, getUserSettings } from '@/api/auth';
import {
  canBankEvent,
  canUseSystemMode,
  countBets,
  flattenBetslipSelections,
  isSelectionInTicket,
  ticketTypeToMode,
  type BetslipMode,
  type BetslipSelectionDraft,
  type BetslipSelectionKeyParts,
  type BetslipTicket,
  type BetslipToggleOptions,
  type BetslipToggleResult,
} from '@/domain/betslip';
import { resolveSystemLimitsFromSettings } from './build-draft';
import {
  betslipClear,
  betslipRemove,
  betslipRemoveCustomPackage,
  betslipRemoveCustomSelection,
  betslipSetMode,
  betslipSetStake,
  betslipSetSystemMin,
  betslipToggle,
  betslipToggleBanked,
  getBetslipServerSnapshot,
  getBetslipSnapshot,
  subscribeBetslip,
} from './betslip-store';
import { useCustomBetPricing } from './useCustomBetPricing';

export function useBetslipTicket(): BetslipTicket {
  return useSyncExternalStore(subscribeBetslip, getBetslipSnapshot, getBetslipServerSnapshot);
}

export function useBetslip() {
  const ticket = useBetslipTicket();
  const mode = ticketTypeToMode(ticket.Type);
  const selectionCount = countBets(ticket);
  const selections = useMemo(() => flattenBetslipSelections(ticket), [ticket]);
  const systemLimits = useMemo(
    () => resolveSystemLimitsFromSettings(getUserSettings(), getAccount()),
    // Recompute when ticket changes (login may have updated settings mid-session).
    [ticket],
  );
  const { refreshCustomBetPricing } = useCustomBetPricing();

  const toggle = useCallback(
    (draft: BetslipSelectionDraft, options?: BetslipToggleOptions): BetslipToggleResult =>
      betslipToggle(draft, options),
    [],
  );
  const remove = useCallback((key: BetslipSelectionKeyParts) => betslipRemove(key), []);
  const removeCustomPackage = useCallback(
    (eventId: string | number) => {
      betslipRemoveCustomPackage(eventId);
      void refreshCustomBetPricing(eventId);
    },
    [refreshCustomBetPricing],
  );
  const removeCustomLeg = useCallback(
    (eventId: string | number, selectionKey: string) => {
      betslipRemoveCustomSelection(eventId, selectionKey);
      void refreshCustomBetPricing(eventId);
    },
    [refreshCustomBetPricing],
  );
  const clear = useCallback(() => betslipClear(ticket.Stake || undefined), [ticket.Stake]);
  const setStakeValue = useCallback((stake: number) => betslipSetStake(stake), []);
  const setModeValue = useCallback((next: BetslipMode) => betslipSetMode(next), []);
  const setSystemMinValue = useCallback(
    (systemMin: number) => betslipSetSystemMin(systemMin),
    [],
  );
  const toggleBankedValue = useCallback(
    (eventId: string | number) => betslipToggleBanked(eventId),
    [],
  );

  return {
    ticket,
    mode,
    selectionCount,
    selections,
    maxOdds: ticket.Odds ?? 0,
    possibleWin: ticket.PossibleWin ?? 0,
    possibleWinPending: Boolean(ticket.PossibleWinPending),
    stake: ticket.Stake,
    stakePerBet: ticket.StakePerBet,
    systemMin: ticket.SystemMin,
    systemMax: ticket.SystemMax,
    systemLimits,
    canUseSystem: canUseSystemMode(ticket, systemLimits),
    canBank: (eventId: string | number) => canBankEvent(ticket, eventId, systemLimits),
    toggle,
    remove,
    removeCustomPackage,
    removeCustomLeg,
    clear,
    setStake: setStakeValue,
    setMode: setModeValue,
    setSystemMin: setSystemMinValue,
    toggleBanked: toggleBankedValue,
  };
}

function eventSelectionKey(ticket: BetslipTicket, eventId: string): string {
  const item = ticket.Items.find((entry) => String(entry.Event.EventId) === eventId);
  if (!item) return '';
  return item.Bets.map((bet) => `${bet.MarketId}|${bet.Specifier ?? ''}|${bet.Outcome}`).join(';');
}

/**
 * String key of this event's selections; only changes when picks on this event
 * change, so list rows can skip re-rendering on unrelated betslip updates.
 */
export function useBetslipEventSelectionKey(eventId: string | number): string {
  const id = String(eventId);
  const getSnapshot = useCallback(() => eventSelectionKey(getBetslipSnapshot(), id), [id]);
  return useSyncExternalStore(subscribeBetslip, getSnapshot, () => '');
}

export function useIsBetslipSelectionActive(parts: BetslipSelectionKeyParts): boolean {
  const ticket = useBetslipTicket();
  return isSelectionInTicket(ticket, parts);
}
